/**
 * @file src/pages/invoice-creation-wizard/components/SaveDraftModal.jsx
 *
 * What changed from the original:
 *  - console.error removed from catch block
 *  - Escape key closes modal
 *  - Backdrop click closes modal
 *  - isSaving local state removed — the modal calls onSave which is
 *    handleSaveAsDraft from index.jsx. That function manages its own
 *    isSaving state. The local isSaving here was redundant and caused
 *    the button to flicker back to enabled immediately after clicking.
 *  - onSave called with no args — index.jsx handleSaveAsDraft expects none.
 *    The draftName/notes fields are preserved for UX but the backend
 *    currently does not consume them (draft name comes from invoice number).
 *
 * NOTE for index.jsx:
 *  Change: onSave={() => handleSaveDraft(false)}  → opens this modal
 *  The "Save Draft" button in the footer calls handleSaveAsDraft directly.
 *  This modal is shown via the WizardHeader "Save" button.
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Icon from "components/AppIcon";

// ---------------------------------------------------------------------------
// SaveDraftModal
// ---------------------------------------------------------------------------

const SaveDraftModal = ({ onClose, onSave, formData }) => {
  const [draftName, setDraftName] = useState(
    `Draft Invoice ${formData.invoice.number || "Untitled"}`,
  );
  const [notes, setNotes] = useState("");

  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = async () => {
    try {
      await onSave();
      onClose();
    } catch {
      // onSave errors are handled in index.jsx via toast — no action needed here
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-modal="true"
      role="dialog"
      aria-label="Save Draft"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                <Icon name="Save" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground dark:text-white">
                  Save Draft
                </h3>
                <p className="text-sm text-muted-foreground dark:text-gray-300">
                  Save your progress and continue later
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Draft Name */}
          <div>
            <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
              Draft Name
            </label>
            <Input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Enter draft name"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes about this draft..."
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Draft Summary */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="font-medium text-foreground dark:text-white mb-3">
              Draft Summary
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground dark:text-gray-300">
                  Customer:
                </span>
                <span className="text-foreground dark:text-white">
                  {formData.customer.name || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground dark:text-gray-300">
                  Invoice Number:
                </span>
                <span className="text-foreground dark:text-white">
                  {formData.invoice.number || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground dark:text-gray-300">
                  Line Items:
                </span>
                <span className="text-foreground dark:text-white">
                  {formData.lineItems.length} item
                  {formData.lineItems.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground dark:text-gray-300">
                  Total Amount:
                </span>
                <span className="text-primary font-medium">
                  {formData.invoice.currency} {formData.totals.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Auto-save Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon
                name="Clock"
                size={16}
                className="text-blue-600 dark:text-blue-400 mt-0.5"
              />
              <div>
                <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Auto-save Reminder
                </h5>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Your work is automatically saved every 30 seconds. This will
                  save a named draft version.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-600">
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              iconName="Save"
              onClick={handleSave}
              disabled={!draftName.trim()}
            >
              Save Draft
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SaveDraftModal;
